/**
 * Parse a free-text coordinate string, auto-detecting the format.
 *
 * Two families are recognised:
 *   - MGRS grid references, e.g. `33UWP1234567890`, `33U WP 12345 67890`,
 *     `4QFJ1267` (converted via the `mgrs` package's `toPoint`).
 *   - Geographic latitude/longitude, in decimal degrees or degrees-minutes-
 *     seconds, with an optional N/S/E/W hemisphere on each component:
 *       `49.8175, 15.4730`   `49.8175 15.4730`   `-49.8175 15.4730`
 *       `49.8175N 15.4730E`  `N49.8175 E15.4730`
 *       `49°48'03"N, 15°28'23"E`
 *
 * DMS input must be comma-separated (the internal spaces are otherwise
 * ambiguous with the lat/lng separator).
 *
 * Returns `null` when the string cannot be interpreted as either format or
 * when the resulting coordinate is out of range.
 */

import { toPoint } from 'mgrs';

export type CoordinateFormat = 'latlng' | 'mgrs';

export interface ParsedCoordinate {
    lat: number;
    lng: number;
    /** Which format the input was detected as. */
    format: CoordinateFormat;
}

// MGRS: 1-2 digit zone, latitude band letter (C-X, no I/O), a two-letter
// 100km square (A-Z, no I/O), then an even number of digits (0-10 total).
const MGRS_RE = /^\d{1,2}[C-HJ-NP-X][A-HJ-NP-Z]{2}(?:\d{2}){0,5}$/;

interface AnglePart {
    value: number;
    axis: 'lat' | 'lng' | null;
}

function tryParseMgrs(input: string): ParsedCoordinate | null {
    const cleaned = input.replace(/\s+/g, '').toUpperCase();
    if (!MGRS_RE.test(cleaned)) return null;
    try {
        const [lng, lat] = toPoint(cleaned);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return { lat, lng, format: 'mgrs' };
    } catch {
        return null;
    }
}

function parseAngleComponent(str: string): AnglePart | null {
    const s = str.trim();
    if (!s) return null;

    const hem = s.match(/[NSEW]/);
    let axis: 'lat' | 'lng' | null = null;
    let hemNegative = false;
    if (hem) {
        const h = hem[0];
        axis = h === 'N' || h === 'S' ? 'lat' : 'lng';
        hemNegative = h === 'S' || h === 'W';
    }

    const body = s.replace(/[NSEW]/g, ' ');
    const leadingMinus = /^\s*-/.test(body);
    const nums = body.match(/\d+(?:\.\d+)?/g);
    if (!nums || nums.length === 0 || nums.length > 3) return null;

    const deg = parseFloat(nums[0]);
    const min = nums.length > 1 ? parseFloat(nums[1]) : 0;
    const sec = nums.length > 2 ? parseFloat(nums[2]) : 0;
    if (min >= 60 || sec >= 60) return null;

    let value = deg + min / 60 + sec / 3600;
    if (hem ? hemNegative : leadingMinus) value = -value;
    return { value, axis };
}

function tryParseLatLng(input: string): ParsedCoordinate | null {
    const norm = input
        .trim()
        .toUpperCase()
        .replace(/[°º'′`"″]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!norm) return null;

    // Comma-separated components allow internal spaces (e.g. DMS); otherwise
    // each component is a single whitespace-delimited token such as
    // `49.8175`, `49.8175N`, or `N49.8175`.
    const comps = (norm.includes(',') ? norm.split(',') : norm.split(/\s+/))
        .map((c) => c.trim())
        .filter(Boolean);
    if (comps.length !== 2) return null;

    const p1 = parseAngleComponent(comps[0]);
    const p2 = parseAngleComponent(comps[1]);
    if (!p1 || !p2) return null;

    let latPart: AnglePart;
    let lngPart: AnglePart;
    if (p1.axis && p2.axis && p1.axis !== p2.axis) {
        latPart = p1.axis === 'lat' ? p1 : p2;
        lngPart = p1.axis === 'lng' ? p1 : p2;
    } else if (p1.axis === 'lng' || p2.axis === 'lat') {
        // A known axis indicates the components are in lng, lat order.
        latPart = p2;
        lngPart = p1;
    } else {
        // Default convention: latitude first, longitude second.
        latPart = p1;
        lngPart = p2;
    }

    const lat = latPart.value;
    const lng = lngPart.value;
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return { lat, lng, format: 'latlng' };
}

/**
 * Parse a coordinate string, auto-detecting MGRS vs. geographic lat/lng.
 * Returns `null` if the input cannot be interpreted.
 */
export function parseCoordinateInput(raw: string): ParsedCoordinate | null {
    const input = raw.trim();
    if (!input) return null;
    return tryParseMgrs(input) ?? tryParseLatLng(input);
}
